import { useEffect, useRef, useState } from 'react';
import * as Yup from 'yup';
import {
  Controller,
  useForm,
  useFieldArray,
  useWatch,
} from 'react-hook-form';
import {
  useAuthHttp,
  useCombinedAllocationPreview,
  ProductAllocation,
} from '@hooks';
import { nowLocalDateTimeInput } from '@utils';
import {
  AVAILABLE_WAREHOUSES_URL,
  CUSTOMER_GET,
  SUGGESTED_ID_URL,
} from '@constants';
import { MdAddCircleOutline, MdErrorOutline } from 'react-icons/md';
import { yupResolver } from '@hookform/resolvers/yup';
import { Customer } from '@views/CustomerMaster/customerRecord';
import { SkuFieldRow } from '@forms/CreateInboundShipmentNoteForm/SkuFieldRow';
import { SkuField } from '@forms/CreateInboundShipmentNoteForm/types';
import { Input } from '../../tailwindComponents/Input/Input';
import { Button } from '../../tailwindComponents/Button';
import { Autocomplete } from '../../tailwindComponents/Autocomplete/Autocomplete';
import { FormPage } from '../../tailwindComponents/FormPage';
import { Subheader } from '../../tailwindComponents/Subheader';
import { Textarea } from '../../tailwindComponents/Textarea/Textarea';
import {
  AvailableWarehouse,
  CreateOutboundShipmentNoteFormValues,
  PreSelectedSku,
} from './types';
import { CreateCustomerModal } from './CreateCustomerModal';
import { ProductFieldRow } from './ProductFieldRow';

export const skuSchema = Yup.object().shape({
  palletising: Yup.object().shape({
    palletisingId: Yup.object().shape({
      label: Yup.string().required('Pallet is required'),
      value: Yup.string().required('Pallet is required'),
    }),
  }),
  skuId: Yup.string()
    .required('SKU is required')
    .test(
      'has-stock',
      'This SKU does not have stock in the selected warehouse',
      function (value) {
        const { parent, options } = this;
        const { warehouseId, stockByWarehouse } = options.context || {};

        if (!value || !warehouseId || !stockByWarehouse) return true;

        return stockByWarehouse.some(
          (stock: any) =>
            stock.warehouseId === warehouseId && stock.availableStock > 0
        );
      }
    ),
  skuQuantity: Yup.string()
    .required('Quantity is required')
    .test(
      'min-if-present',
      'Must be at least 1',
      (value) => !value || Number(value) >= 1
    )
    .matches(/^\d*\.?\d*$/, 'Must be a valid number'),
});

export const productSchema = Yup.object().shape({
  productId: Yup.string().required('Product is required'),
  productName: Yup.string(),
  productQuantity: Yup.string()
    .required('Quantity is required')
    .test('min-if-present', 'Must be at least 1', (value) =>
      !value ? false : Number(value) >= 1
    )
    .matches(/^\d*\.?\d*$/, 'Must be a valid number'),
});

const inventorySkuIdsOfChosenSkuLines = (skuFields: SkuField[] | undefined) =>
  (skuFields ?? [])
    .filter((sku) => sku?.skuId)
    .map((sku) => sku.id)
    .filter(Boolean);

export const createDespatchNoteSchema = Yup.object()
  .shape({
    skuFields: Yup.array().of(skuSchema),
    productFields: Yup.array().of(productSchema),
    despatchNoteNumber: Yup.string().required(
      'Despatch note number is required'
    ),
    purchaseOrderNumber: Yup.string().required(
      'Purchase order number is required'
    ),
    customerName: Yup.string().required('Customer is required'),
    warehouseName: Yup.string().required('Warehouse is required'),
    requiredTimeOfDespatch: Yup.string().required(
      'Required time of despatch is required'
    ),
    comment: Yup.string(),
  })
  .test(
    'at-least-one-line',
    'Add at least one SKU or product',
    (value) =>
      (value.skuFields?.length || 0) + (value.productFields?.length || 0) >= 1
  )
  .test(
    'unique-products',
    'Each product can only be added once — combine the quantities into a single line.',
    (value) => {
      const ids = (value.productFields || [])
        .map((product: any) => product?.productId)
        .filter(Boolean);
      return new Set(ids).size === ids.length;
    }
  )
  .test(
    'unique-skus',
    'Each SKU can only be added once — combine the quantities into a single line.',
    (value) => {
      const inventorySkuIds = inventorySkuIdsOfChosenSkuLines(
        value.skuFields as SkuField[] | undefined
      );
      return new Set(inventorySkuIds).size === inventorySkuIds.length;
    }
  );

export function CreateOutboundShipmentNoteForm({
  onSubmit,
  isSubmitting,
  header,
  pages,
  preSelectedSku,
  preSelectedUnit = 'product',
  submitError,
  onDismissError,
}: {
  onSubmit: (data: CreateOutboundShipmentNoteFormValues) => void;
  isSubmitting: boolean;
  header: string;
  pages: { name: string; href: string; current: boolean }[];
  preSelectedSku?: PreSelectedSku;
  preSelectedUnit?: 'product' | 'sku';
  submitError?: string | null;
  onDismissError?: () => void;
}) {
  const authHttp = useAuthHttp();
  const [customerAddress, setCustomerAddress] = useState<string | null>(null);
  const [warehouseAddress, setWarehouseAddress] = useState<string | null>(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [loadingAvailableWarehouses, setLoadingAvailableWarehouses] =
    useState(false);
  const [availableWarehouseOptions, setAvailableWarehouseOptions] = useState<
    AvailableWarehouse[]
  >([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [isCreateCustomerModalOpen, setIsCreateCustomerModalOpen] = useState(false);

  const isProductMode = preSelectedUnit === 'product';

  const methods = useForm<CreateOutboundShipmentNoteFormValues>({
    shouldUnregister: false,
    resolver: yupResolver(createDespatchNoteSchema),
    mode: 'onChange',
    defaultValues: {
      despatchNoteNumber: '',
      purchaseOrderNumber: '',
      customerId: '',
      customerName: '',
      warehouseId: '',
      warehouseName: '',
      requiredTimeOfDespatch: nowLocalDateTimeInput(),
      skuFields: isProductMode
        ? []
        : [
            preSelectedSku
              ? {
                  palletising: { palletisingId: { label: '', value: '' } },
                  id: preSelectedSku.inventorySkuId || '',
                  skuId: preSelectedSku.skuId || '',
                  skuQuantity: '',
                  stockByWarehouse: preSelectedSku.stockByWarehouse || [],
                }
              : {
                  palletising: { palletisingId: { label: '', value: '' } },
                  id: '',
                  skuId: '',
                  skuQuantity: '',
                  stockByWarehouse: [],
                },
          ],
      productFields: isProductMode
        ? [
            {
              productId: preSelectedSku?.productDetails?.productId || '',
              productName: preSelectedSku?.productDetails?.name || '',
              productQuantity: '',
            },
          ]
        : [],
      comment: '',
    },
  });

  const { formState, control, setValue } = methods;

  useEffect(() => {
    async function getAvailableWarehouses() {
      setLoadingAvailableWarehouses(true);
      try {
        const {
          data: { matches },
        } = await authHttp.post(AVAILABLE_WAREHOUSES_URL, {
          pagination: {
            lastItemSort: null,
            itemsPerPage: 25,
          },
        });
        setAvailableWarehouseOptions(matches);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error);
      } finally {
        setLoadingAvailableWarehouses(false);
      }
    }
    async function fetchSuggestedID() {
      try {
        const { data } = await authHttp.get(SUGGESTED_ID_URL);

        if (data.suggestedId) setValue('despatchNoteNumber', data.suggestedId);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('error: ', error);
      }
    }
    async function fetchCustomers() {
      try {
        const { data } = await authHttp.post(CUSTOMER_GET, {
          pagination: {
            itemsPerPage: 100,
          },
        });

        if (data) setCustomers(data.customers);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error);
      }
    }
    fetchCustomers();
    fetchSuggestedID();
    getAvailableWarehouses();
  }, []);

  const handleCustomerCreated = (newCustomer: Customer) => {
    setCustomers((prev) => [...prev, newCustomer]);
    setValue('customerId', String(newCustomer.id));
    setValue('customerName', newCustomer.name);
    setCustomerAddress(newCustomer.address);
    setTimeout(() => {
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }
    }, 0);
  };

  const {
    fields: skuFields,
    append: appendSku,
    remove: removeSku,
  } = useFieldArray({
    control,
    name: 'skuFields',
  });

  const {
    fields: productFields,
    append: appendProduct,
    remove: removeProduct,
  } = useFieldArray({
    control,
    name: 'productFields',
  });

  const selectedWarehouseId = useWatch({ control, name: 'warehouseId' });

  const watchedSkuFields = useWatch({ control, name: 'skuFields' }) || [];
  const watchedProductFields = useWatch({ control, name: 'productFields' }) || [];

  const skuLines = (watchedSkuFields as any[])
    .map((sku) => ({ skuId: sku?.id as string, skuQuantity: Number(sku?.skuQuantity) }))
    .filter((line) => line.skuId && Number.isFinite(line.skuQuantity) && line.skuQuantity >= 1);

  const productLines = (watchedProductFields as any[])
    .map((product) => ({
      productId: product?.productId as string,
      productQuantity: Number(product?.productQuantity),
    }))
    .filter(
      (line) =>
        line.productId &&
        Number.isFinite(line.productQuantity) &&
        line.productQuantity >= 1
    );

  const {
    previews,
    loading: previewLoading,
    error: previewError,
    stale: previewStale,
  } = useCombinedAllocationPreview(selectedWarehouseId, skuLines, productLines);

  const previewByProductId = new Map<string, ProductAllocation>();
  previews.forEach((product) => {
    if (product) previewByProductId.set(product.productId, product);
  });

  const hasProductLines = productLines.length > 0;

  const hasInsufficientStock = previews.some(
    (product) => product && !product.hasEnoughStock
  );

  const previewBlocksSubmit =
    hasProductLines &&
    (previewStale || previewLoading || !!previewError || hasInsufficientStock);

  const skuLinesKey = JSON.stringify(skuLines);
  const productLinesKey = JSON.stringify(productLines);

  const onDismissErrorRef = useRef(onDismissError);
  onDismissErrorRef.current = onDismissError;
  useEffect(() => {
    onDismissErrorRef.current?.();
  }, [skuLinesKey, productLinesKey]);

  const selectedProductIds = (watchedProductFields as any[])
    .map((product) => product?.productId as string)
    .filter(Boolean);

  const selectedInventorySkuIds =
    inventorySkuIdsOfChosenSkuLines(watchedSkuFields);

  let previewBlockMessage: string | null = null;
  if (hasProductLines && !previewLoading) {
    if (hasInsufficientStock) {
      previewBlockMessage =
        'One or more product lines do not have enough stock in this warehouse. Reduce the quantities to continue.';
    } else if (previewError) {
      previewBlockMessage =
        'Could not check stock availability for the products. Please retry before submitting.';
    }
  }

  const preselectedSkuFieldId = useRef(skuFields[0]?.id);
  const preselectedProductFieldId = useRef(productFields[0]?.id);

  const skuRows = skuFields.map((sku, index) => (
    <SkuFieldRow
      key={`sku-${sku.id}`}
      sku={sku}
      index={index}
      control={control}
      removeSku={removeSku}
      skuFields={skuFields}
      formState={formState}
      setValue={setValue}
      selectedWarehouse={methods.getValues('warehouseId')}
      setError={methods.setError}
      clearErrors={methods.clearErrors}
      availableWarehouseOptions={availableWarehouseOptions}
      setWarehouseAddress={setWarehouseAddress}
      allowRemoveAll
      compact
      showDetailsPanel
      preloadOptions
      selectedInventorySkuIds={selectedInventorySkuIds}
      {...(!isProductMode &&
      preSelectedSku &&
      sku.id === preselectedSkuFieldId.current
        ? { initialSkuOption: preSelectedSku }
        : {})}
    />
  ));

  const productRows = productFields.map((product, index) => (
    <ProductFieldRow
      key={`product-${product.id}`}
      index={index}
      control={control}
      removeProduct={removeProduct}
      formState={formState}
      setValue={setValue}
      warehouseId={selectedWarehouseId}
      availableWarehouseOptions={availableWarehouseOptions}
      setWarehouseAddress={setWarehouseAddress}
      showDetailsPanel
      preview={
        previewByProductId.get(
          (watchedProductFields as any[])[index]?.productId
        ) ?? null
      }
      previewLoading={previewLoading}
      previewError={previewError}
      selectedProductIds={selectedProductIds}
      {...(isProductMode &&
      preSelectedSku &&
      product.id === preselectedProductFieldId.current
        ? { initialProductOption: preSelectedSku }
        : {})}
    />
  ));

  return (
    <>
    <FormPage<CreateOutboundShipmentNoteFormValues>
      header={header}
      pages={pages}
      methods={methods}
      currentStep={currentStep}
      setCurrentStep={setCurrentStep}
      onSubmit={(data) => {
        onSubmit(data);
      }}
      isSubmitting={isSubmitting}
      canSubmit={!previewBlocksSubmit}
      submitError={previewBlockMessage || submitError}
      onDismissError={previewBlockMessage ? undefined : onDismissError}
    >
      <Subheader subHeader='Outbound Shipment Note Details' />
      <div className='flex flex-col gap-4 mt-2'>
        {isProductMode ? (
          <>
            {productRows}
            {skuRows}
          </>
        ) : (
          <>
            {skuRows}
            {productRows}
          </>
        )}
        {skuFields.length === 0 && productFields.length === 0 && (
          <div className='flex items-center justify-center gap-2 rounded-lg border border-amber-200 bg-amber-50 p-6 text-sm font-medium text-amber-700'>
            <MdErrorOutline size={20} className='shrink-0' />
            Add at least one SKU or product to despatch.
          </div>
        )}
        <div className='flex flex-wrap gap-3'>
          <Button
            intent='secondary'
            size='sm'
            label='Add product'
            startIcon={<MdAddCircleOutline size={20} />}
            onClick={() =>
              appendProduct({
                productId: '',
                productName: '',
                productQuantity: '',
              })
            }
          />
          <Button
            intent='secondary'
            size='sm'
            label='Add SKU'
            startIcon={<MdAddCircleOutline size={20} />}
            onClick={() =>
              appendSku({
                palletising: {
                  palletisingId: {
                    label: '',
                    value: '',
                  },
                },
                id: '',
                skuId: '',
                skuQuantity: '',
              })
            }
          />
        </div>
      </div>
      <div className='grid grid-cols-1 md:grid-cols-2 gap-4 mt-2'>
        <div className='flex flex-col gap-y-4'>
          <Controller
            name='despatchNoteNumber'
            control={control}
            render={({ field }) => (
              <Input
                {...field}
                label='Outbound shipment note number'
                name='outboundShipmentNoteNumber'
                isInvalid={!!formState.errors.despatchNoteNumber}
                validationMessage={
                  formState.errors.despatchNoteNumber?.message as string
                }
                required
              />
            )}
          />
          <Controller
            name='purchaseOrderNumber'
            control={control}
            render={({ field }) => (
              <Input
                {...field}
                label='Purchase order number'
                name='purchaseOrderNumber'
                isInvalid={!!formState.errors.purchaseOrderNumber}
                validationMessage={
                  formState.errors.purchaseOrderNumber?.message as string
                }
                required
              />
            )}
          />
          <Controller
            name='customerName'
            control={control}
            render={({ field }) => (
              <Autocomplete
                label='Customer name'
                options={customers.map((c) => ({ label: c.name, ...c }))}
                value={field.value}
                onInputChange={(val) => field.onChange(val)}
                onChange={(option) => {
                  if (!option) return;
                  setValue('customerId', String(option.id));
                  field.onChange(option.label);
                  setCustomerAddress(option.address);
                }}
                helperText={
                  customerAddress ? `Customer Address: ${customerAddress}` : ''
                }
                isInvalid={!!formState.errors.customerName}
                validationMessage={
                  formState.errors.customerName?.message as string
                }
                actionLabel='+ Create New Customer'
                onActionClick={() => setIsCreateCustomerModalOpen(true)}
              />
            )}
          />
        </div>
        <div className='flex flex-col gap-y-4'>
          <Controller
            name='warehouseName'
            control={control}
            render={({ field }) => (
              <Autocomplete
                label='From warehouse'
                options={availableWarehouseOptions.map((w) => ({
                  id: w.warehouse.warehouseId,
                  label: w.warehouse.name,
                  ...w.warehouse,
                }))}
                loading={loadingAvailableWarehouses}
                value={field.value}
                onInputChange={() => {}}
                onChange={(option) => {
                  if (!option) return;
                  setValue('warehouseId', option.warehouseId);
                  field.onChange(option.label);
                  setWarehouseAddress(option.address);
                }}
                helperText={
                  warehouseAddress
                    ? `Warehouse Address: ${warehouseAddress}`
                    : ''
                }
                isInvalid={!!formState.errors.warehouseId}
                validationMessage={
                  formState.errors.warehouseId?.message as string
                }
              />
            )}
          />
          <Controller
            name='requiredTimeOfDespatch'
            control={control}
            render={({ field }) => (
              <Input
                {...field}
                label='Required time of despatch'
                name='requiredTimeOfDespatch'
                type='datetime-local'
                isInvalid={!!formState.errors.requiredTimeOfDespatch}
                validationMessage={
                  formState.errors.requiredTimeOfDespatch?.message as string
                }
              />
            )}
          />
        </div>
      </div>
      <div className='w-full mt-2'>
        <Controller
          name='comment'
          control={control}
          render={({ field }) => (
            <Textarea {...field} label='Comment' name='comment' optional />
          )}
        />
      </div>
    </FormPage>
    <CreateCustomerModal
      isOpen={isCreateCustomerModalOpen}
      onClose={() => setIsCreateCustomerModalOpen(false)}
      onCustomerCreated={handleCustomerCreated}
    />
    </>
  );
}
